import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowUpCircle, Check, X } from "lucide-react";

export interface PlanEnforcementError {
  errorCode: "UPGRADE_REQUIRED";
  message: string;
  currentPlan: string;
  requiredPlan?: string;
  feature: string;
  limitReached?: {
    current: number;
    limit: number;
  };
}

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  error: PlanEnforcementError | null;
}

const planNames: Record<string, string> = {
  lite: "Lite",
  core: "Core",
  pro: "Pro",
  unlimited: "Unlimited",
};

const planOrder = ["lite", "core", "pro", "unlimited"];

export function UpgradeModal({ open, onOpenChange, error }: UpgradeModalProps) {
  if (!error) return null;

  const currentPlanIndex = planOrder.indexOf(error.currentPlan);
  const requiredPlanIndex = error.requiredPlan ? planOrder.indexOf(error.requiredPlan) : -1;
  
  const availablePlans = planOrder.slice(currentPlanIndex + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="modal-upgrade">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-primary" />
            Upgrade Required
          </DialogTitle>
          <DialogDescription>
            {error.message}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error.limitReached && (
            <div className="border rounded-md p-4 space-y-2" data-testid="limit-info">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Current Usage</span>
                <Badge variant="secondary" data-testid="badge-current-usage">
                  {error.limitReached.current} / {error.limitReached.limit}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                You've reached your plan limit for {error.feature}.
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <div className="text-sm font-semibold mb-3">Your Current Plan</div>
            <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50">
              <span className="font-medium">{planNames[error.currentPlan] || error.currentPlan}</span>
              <Badge variant="outline" data-testid="badge-current-plan">Current</Badge>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="text-sm font-semibold mb-3">Available Upgrades</div>
            <div className="space-y-2">
              {availablePlans.map((plan) => {
                const isRecommended = plan === error.requiredPlan;
                return (
                  <div 
                    key={plan} 
                    className={`flex items-center justify-between p-3 border rounded-md ${
                      isRecommended ? 'border-primary bg-primary/5' : ''
                    }`}
                    data-testid={`plan-option-${plan}`}
                  >
                    <span className="font-medium">{planNames[plan] || plan}</span>
                    {isRecommended && (
                      <Badge variant="default" data-testid="badge-recommended">
                        <Check className="w-3 h-3 mr-1" />
                        Recommended
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-muted/50 rounded-md p-4 text-sm text-muted-foreground">
            Contact your administrator or visit the billing page to upgrade your plan and unlock additional features.
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            data-testid="button-cancel-upgrade"
          >
            Close
          </Button>
          <Button 
            onClick={() => {
              window.location.href = '/settings/billing';
            }}
            data-testid="button-view-plans"
          >
            View Plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
